/**
 * <greeting-card-drawer> custom element.
 * Design selector + 3D card animation + contenteditable message + gc:save dispatch.
 */

class GreetingCardDrawer extends HTMLElement {
  _variants = [];
  _selectedVariant = null;
  #openTimer = null;
  #innerTimer = null;
  #resetTimer = null;

  disconnectedCallback() {
    if (this.#openTimer) clearTimeout(this.#openTimer);
    if (this.#innerTimer) clearTimeout(this.#innerTimer);
    if (this.#resetTimer) clearTimeout(this.#resetTimer);
  }

  connectedCallback() {
    this.#parseProductData();
    this.#bindDesignSelector();
    this.#bindNext();
    this.#bindMessage();
    this.#bindSave();
    this.#bindChange();
    this.#applyTemplateStyles();
    this.#updateCharCount();
  }

  #parseProductData() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;
    const data = JSON.parse(script.textContent);
    this._variants = data.variants || [];
    const checked = this.querySelector('[data-gc-design][aria-checked="true"]');
    const initialId = checked ? Number(checked.dataset.variantId) : this._variants[0]?.id;
    this._selectedVariant = this._variants.find((v) => v.id === initialId) || this._variants[0];
  }

  #bindDesignSelector() {
    const group = this.querySelector("[data-gc-designs]");
    if (!group) return;
    const buttons = [...this.querySelectorAll("[data-gc-design]")];

    group.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-gc-design]");
      if (!btn) return;
      this.#selectDesign(btn, buttons);
    });

    group.addEventListener("keydown", (e) => {
      if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"].includes(e.key)) return;
      e.preventDefault();

      const current = buttons.findIndex((b) => b.getAttribute("aria-checked") === "true");
      const next =
        e.key === "ArrowRight" || e.key === "ArrowDown"
          ? (current + 1) % buttons.length
          : (current - 1 + buttons.length) % buttons.length;

      this.#selectDesign(buttons[next], buttons);
      buttons[next].focus();
    });
  }

  #selectDesign(btn, buttons) {
    for (const b of buttons) {
      b.setAttribute("aria-checked", "false");
      b.setAttribute("tabindex", "-1");
    }
    btn.setAttribute("aria-checked", "true");
    btn.setAttribute("tabindex", "0");
    const variantId = Number(btn.dataset.variantId);
    this._selectedVariant = this._variants.find((v) => v.id === variantId);
    this.#applyTemplateStyles();
    this.#updateCharCount();
  }

  #bindNext() {
    const btn = this.querySelector("[data-gc-next]");
    if (!btn) return;
    btn.addEventListener("click", () => {
      if (!this._selectedVariant) return;
      this.#openCardStage();
    });
  }

  #openCardStage() {
    const coverImg = this.querySelector("[data-gc-cover-img]");
    const card = this.querySelector("[data-gc-card]");
    const stage = this.querySelector("[data-gc-stage]");
    const picker = this.querySelector("[data-gc-picker]");

    if (!coverImg || !card || !stage) return;

    const imgUrl = this._selectedVariant?.image || "";
    coverImg.src = imgUrl;

    card.classList.remove("open", "lifted");
    if (this.#openTimer) clearTimeout(this.#openTimer);
    if (this.#innerTimer) clearTimeout(this.#innerTimer);

    if (picker) {
      picker.hidden = true;
      picker.setAttribute("inert", "");
    }

    const counter = this.querySelector("[data-gc-char-count]");
    if (counter) counter.hidden = false;

    const saveBtn = this.querySelector("[data-gc-save]");
    if (saveBtn?.dataset.i18nSave) saveBtn.textContent = saveBtn.dataset.i18nSave;

    const changeBtn = this.querySelector("[data-gc-change]");
    if (changeBtn) changeBtn.hidden = false;

    const nextBtn = this.querySelector("[data-gc-next]");
    if (nextBtn) nextBtn.hidden = true;

    stage.classList.remove("visible");
    stage.setAttribute("aria-hidden", "true");
    void stage.offsetWidth;
    stage.classList.add("visible");
    stage.removeAttribute("aria-hidden");

    this.#openTimer = setTimeout(() => {
      card.classList.add("open");

      this.#innerTimer = setTimeout(() => {
        card.classList.add("lifted");
        this.#setStep(2);
        const msg = this.querySelector("[data-gc-message]");
        if (msg) msg.focus();
        this.#announce(
          this.dataset.i18nAnnounceCardOpen || "Card preview open. Write your greeting.",
        );
      }, 950);
    }, 450);
  }

  #bindMessage() {
    const msg = this.querySelector("[data-gc-message]");
    if (!msg) return;

    msg.addEventListener("beforeinput", (e) => {
      const max = this.#getMaxChars();
      const selection = window.getSelection();
      const selectedLen =
        selection && selection.rangeCount > 0 && msg.contains(selection.anchorNode)
          ? selection.toString().length
          : 0;
      const currentLen = (msg.innerText || "").length;
      const insertedLen = (e.data || "").length;
      if (currentLen - selectedLen + insertedLen <= max) return;

      e.preventDefault();
      if (e.inputType !== "insertText" && e.inputType !== "insertFromPaste") return;

      const allowed = Math.max(0, max - (currentLen - selectedLen));
      if (allowed === 0) return;
      const truncated = (e.data || "").slice(0, allowed);
      document.execCommand("insertText", false, truncated);
    });

    msg.addEventListener("input", () => {
      const max = this.#getMaxChars();
      const text = msg.innerText || "";
      if (text.length > max) {
        msg.innerText = text.slice(0, max);
        const range = document.createRange();
        range.selectNodeContents(msg);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      this.#updateCharCount();
    });
  }

  #getMaxChars() {
    return this._selectedVariant?.cardTemplate?.maxChars || 200;
  }

  #updateCharCount() {
    const msg = this.querySelector("[data-gc-message]");
    const counter = this.querySelector("[data-gc-char-count]");
    if (!msg || !counter) return;
    const max = this.#getMaxChars();
    const text = msg.innerText || "";
    const remaining = Math.max(0, max - text.length);
    const suffix = this.dataset.i18nCharsRemaining || "characters remaining";
    counter.textContent = `${remaining} ${suffix}`;
    counter.classList.toggle("gc-char-count--over", text.length > max);
  }

  #applyTemplateStyles() {
    const msg = this.querySelector("[data-gc-message]");
    if (!msg) return;
    const font = this._selectedVariant?.cardTemplate?.font;
    msg.style.color = font?.color || "";
    msg.style.fontFamily = font?.family || "";
    msg.style.lineHeight = font?.size && font?.lineHeight
      ? String(font.lineHeight / font.size)
      : "";
  }

  #bindSave() {
    const btn = this.querySelector("[data-gc-save]");
    if (!btn) return;
    btn.addEventListener("click", () => this.#save());
  }

  #save() {
    const msg = this.querySelector("[data-gc-message]");
    const message = (msg?.innerText || "").slice(0, this.#getMaxChars());
    const uuid = crypto.randomUUID();

    this.dispatchEvent(
      new CustomEvent("gc:save", {
        bubbles: true,
        detail: {
          variantId: this._selectedVariant?.id,
          designVariantId: this._selectedVariant?.id,
          message,
          uuid,
          designImage: this._selectedVariant?.image || "",
        },
      }),
    );

    this.#closeDrawer();
  }

  #bindChange() {
    const btn = this.querySelector("[data-gc-change]");
    if (!btn) return;
    btn.addEventListener("click", () => this.#resetCard());
  }

  #resetCard() {
    const card = this.querySelector("[data-gc-card]");
    const stage = this.querySelector("[data-gc-stage]");
    const picker = this.querySelector("[data-gc-picker]");

    if (this.#openTimer) clearTimeout(this.#openTimer);
    if (this.#innerTimer) clearTimeout(this.#innerTimer);
    if (this.#resetTimer) clearTimeout(this.#resetTimer);
    if (card) card.classList.remove("open", "lifted");

    this.#resetTimer = setTimeout(() => {
      if (stage) {
        stage.classList.remove("visible");
        stage.setAttribute("aria-hidden", "true");
      }
      if (picker) {
        picker.hidden = false;
        picker.removeAttribute("inert");
      }
      const counter = this.querySelector("[data-gc-char-count]");
      if (counter) counter.hidden = true;
      const saveBtn = this.querySelector("[data-gc-save]");
      if (saveBtn?.dataset.i18nSaveEmpty) saveBtn.textContent = saveBtn.dataset.i18nSaveEmpty;
      const changeBtn = this.querySelector("[data-gc-change]");
      if (changeBtn) changeBtn.hidden = true;
      const nextBtn = this.querySelector("[data-gc-next]");
      if (nextBtn) nextBtn.hidden = false;
      this.#setStep(1);
      this.#announce(
        this.dataset.i18nAnnouncePickDesign || "Choose a cover photo to continue.",
      );
    }, 1000);
  }

  #setStep(n) {
    const steps = this.querySelectorAll("[data-gc-step]");
    steps.forEach((el) => {
      const stepNum = Number(el.dataset.gcStep);
      el.classList.toggle("active", stepNum === n);
      el.classList.toggle("done", stepNum < n);
      if (stepNum === n) el.setAttribute("aria-current", "step");
      else el.removeAttribute("aria-current");
    });
  }

  #announce(msg) {
    const region = this.querySelector("[data-gc-live-region]");
    if (!region) return;
    region.textContent = "";
    requestAnimationFrame(() => { region.textContent = msg; });
  }

  #closeDrawer() {
    const dialog = this.closest("dialog-component");
    if (dialog?.closeDialog) dialog.closeDialog();
  }

  /**
   * Restore previously saved state (Edit flow).
   * @param {{ variantId: number, message: string }} state
   */
  restoreState(state) {
    const buttons = [...this.querySelectorAll("[data-gc-design]")];
    const target = buttons.find((b) => Number(b.dataset.variantId) === state.variantId);
    if (target) {
      this.#selectDesign(target, buttons);
      this.#openCardStage();
    }

    const msg = this.querySelector("[data-gc-message]");
    if (msg) {
      msg.innerText = state.message ?? "";
      this.#updateCharCount();
    }
  }
}

if (!customElements.get("greeting-card-drawer")) {
  customElements.define("greeting-card-drawer", GreetingCardDrawer);
}
